import React from 'react';
import {emptyArray} from '@packages/utilities/fp/utils';
import {
  useDispatch,
  useCssVariables,
  useCssVariableCollections,
  useCurrentLocale,
  usePreviewModeFrameId,
  useSlugMap,
  useMediaQuery,
} from '@packages/systems/render';
import {LocaleUtils} from '@packages/systems/localization';
import {
  useFrameSizeTransition,
  PrimitiveFrame,
  getBreakpointWidth,
  getBreakpointHeight,
} from '@packages/systems/open-canvas/ui';
import {
  componentCanvasActiveFrameMeasured,
  getMinMaxFrameWidthForBreakpoint,
} from '@packages/systems/component-canvas/designer';
import type {BindableColorValue} from '@packages/systems/component-canvas/types';
import {getBreakpointID} from '@packages/systems/style/types';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {
  Atom,
  Plugins,
  withSystem,
  AtomRenderProps,
} from '@packages/systems/wfdl';
import {TListOfElement, TTypeConstructor} from '@packages/systems/wfdl-ast';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {getChildren} from '@packages/utilities/data-utils';

import FrameLayout from './FrameLayout';
import {
  getMeasuredFrameDimensionAttributes,
  getUserSuppliedDimensions,
  useMeasuredFrameDimensionStyles,
} from './FrameMeasurements';
import {FrameSizeType} from './FrameSize';
import {
  FrameBackgroundColorType,
  BindableColorType,
} from './FrameBackgroundColor';
import {
  renderAnyCssValue,
  VariableCollectionMap,
  VariableMap,
} from '@packages/systems/core/utils/StyleVariables';
import {hslaToCssString} from './hslaToCssString';
import {useBodyBackgroundColor} from './utils/selectBodyBackgroundColor';

export function renderColorExpressionValue(
  value: BindableColorValue,
  variables: VariableMap,
  variableCollections: VariableCollectionMap,
  slugMap: Plugins.PluginsType['slugMap'],
  bodyBackgroundColor: string
): string {
  if (value.type === 'ref' || value.type === 'raw') {
    const result = renderAnyCssValue(
      value,
      variables,
      variableCollections,
      slugMap,
      emptyArray
    );
    if (result instanceof Error) {
      return 'white';
    }
    return result;
  }

  if (value.type === 'body-background-color') {
    return bodyBackgroundColor;
  }

  return hslaToCssString(value);
}

const FrameRender = Atom.createRender(
  ({
    attributes,
    classes,
    data,
    generateAttributes,
    renderChild,
  }: AtomRenderProps) => {
    const dispatch = useDispatch();
    const previewFrameId = usePreviewModeFrameId();
    const previewMode = Boolean(previewFrameId);
    const ref = React.useRef<null | HTMLDivElement>(null);

    const handleContextMenu = React.useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        // prevent browser's default context menu on empty space within the Frame element
        // (only if clicking directly on the Frame element itself, not its children)
        if (event.target === event.currentTarget) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      []
    );

    const frameId = attributes.get('data-w-id') as string;
    const isActive = previewFrameId === frameId;

    const mediaQuery = useMediaQuery() ?? 'main';
    const breakpointId = getBreakpointID(mediaQuery) ?? 'main';
    // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
    const backgroundSettings = data.background?.[breakpointId];
    const backgroundColorValue: BindableColorValue | undefined =
      backgroundSettings?.color;
    const cssVariables = useCssVariables();
    const cssVariableCollections = useCssVariableCollections();
    const currentLocale = useCurrentLocale();
    const dir =
      currentLocale && LocaleUtils.isRTL(currentLocale.tag) ? 'rtl' : undefined;
    const slugMap = useSlugMap();
    const bodyBackgroundColor = useBodyBackgroundColor(breakpointId);
    const backgroundColor = backgroundColorValue
      ? renderColorExpressionValue(
          backgroundColorValue,
          cssVariables,
          cssVariableCollections,
          slugMap,
          bodyBackgroundColor
        )
      : bodyBackgroundColor;

    React.useLayoutEffect(() => {
      if (!isActive || !previewMode) return;

      const el = ref.current;
      if (!el) return;

      const bounds = el.getBoundingClientRect();

      dispatch(
        componentCanvasActiveFrameMeasured({
          width: bounds.width,
          height: bounds.height,
        })
      );
    }, [
      isActive,
      previewMode,
      // Re-center element when breakpoint changes
      breakpointId,
      dispatch,
    ]);

    React.useEffect(() => {
      if (!isActive || !previewMode) return;

      const el = ref.current;
      if (!el) return;

      const observer = new ResizeObserver(() => {
        const bounds = el.getBoundingClientRect();
        dispatch(
          componentCanvasActiveFrameMeasured({
            width: bounds.width,
            height: bounds.height,
          })
        );
      });

      observer.observe(el);
      return () => observer.disconnect();
    }, [isActive, previewMode, dispatch]);

    React.useLayoutEffect(() => {
      if (!isActive || !previewMode) return;

      const el = ref.current;
      if (!el) return;

      const body = el.ownerDocument.body;
      const originalBackgroundColor = body.style.backgroundColor;
      body.style.backgroundColor = backgroundColor;

      return () => {
        body.style.backgroundColor = originalBackgroundColor;
      };
    }, [isActive, previewMode, backgroundColor]);

    const frameSizeTransition = useFrameSizeTransition(frameId);

    const measuredDimensionStyles = useMeasuredFrameDimensionStyles({
      frameId: frameId as string,
      breakpointId,
      frameData: data,
    });

    if (previewMode && !isActive) return null;

    const generatedAttributes = generateAttributes(attributes, classes);

    const transitionWidth = frameSizeTransition && frameSizeTransition.width;
    const transitionHeight = frameSizeTransition && frameSizeTransition.height;
    const {minWidth, maxWidth} = getMinMaxFrameWidthForBreakpoint(breakpointId);

    const {width: userSuppliedWidth, height: userSuppliedHeight} =
      getUserSuppliedDimensions(data, breakpointId);

    // Anchor `vh` to the frame's height in Fixed mode and during drag. In Fit
    // mode the frame's height is content-driven, so making it the basis for
    // `vh` would loop: `vh`-using content sizes the frame, frame sizes `vh`,
    // repeat. We fall back to the breakpoint default there.
    const parsedFixedHeight = parseFloat(userSuppliedHeight);
    const concreteFrameHeight =
      typeof transitionHeight === 'number'
        ? transitionHeight
        : Number.isFinite(parsedFixedHeight)
          ? parsedFixedHeight
          : null;

    const frameProps = {
      ...generatedAttributes,
      ...getMeasuredFrameDimensionAttributes({
        breakpointId,
        frameData: data,
      }),
      frameId,
      width: transitionWidth ?? measuredDimensionStyles.width,
      height: transitionHeight ?? measuredDimensionStyles.height,
      minWidth,
      maxWidth,
      backgroundColor,
      vhBase: concreteFrameHeight ?? getBreakpointHeight(breakpointId),
      dir,
      ref,
    } as const;

    if (previewMode) {
      return (
        <PrimitiveFrame {...frameProps}>
          {getChildren(data).map((child) => renderChild(child))}
        </PrimitiveFrame>
      );
    }

    return (
      <PrimitiveFrame {...frameProps}>
        {
          // This extra `div` is a "virtual viewport" wrapper that provides a
          // frame with a fixed width and height when neccessary, ensuring that
          // percentage-based values work consistently across frames:
        }
        <div
          data-wf-frame-viewport="true"
          style={{
            position: 'fixed',
            transform: 'translateX(0px)',
            top: '0px',
            left: '0px',
            // Only if the user-supplied dimensions are `Fit` do we want to
            // provide a viewport-derived dimension:
            width:
              userSuppliedWidth === 'max-content'
                ? getBreakpointWidth(breakpointId)
                : (transitionWidth ?? userSuppliedWidth),
            height:
              userSuppliedHeight === 'max-content'
                ? getBreakpointHeight(breakpointId)
                : (transitionHeight ?? userSuppliedHeight),
            // Inherit min/max width constraints from the frame element
            minWidth: 'inherit',
            maxWidth: 'inherit',
          }}
          onContextMenu={handleContextMenu}
        >
          {getChildren(data).map((child) => renderChild(child))}
        </div>
      </PrimitiveFrame>
    );
  }
);

export const Frame = createIntegratedAtom({
  showEmpty: true,
  displayName: 'Frame',
  render: FrameRender,
  fields: {
    children: withSystem({private: true}, TListOfElement),
    size: TTypeConstructor(
      qualify(Plugins.name('Frame'), typeName('FrameSize'))
    ),
    background: TTypeConstructor(
      qualify(Plugins.name('Frame'), typeName('FrameBackgroundColor'))
    ),
  },
  constraints: {
    parent: [{is: 'FrameLayout', rule: 'ExactlyOne'}],
    pinToParent: true,
  },
});

export const plugin = Plugins.PrivilegedPlugin({
  atoms: {
    Frame,
    FrameLayout,
  },
  dataTypes: {
    FrameSize: FrameSizeType,
    FrameBackgroundColor: FrameBackgroundColorType,
    BindableColor: BindableColorType,
  },
});
